import React, { useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { HELP_QUESTION_MARK } from '../icons';

/** The author's help text for an element (options.help), or '' when none. */
export const elementHelpText = element => {
	const help = element?.options?.help;
	return typeof help === 'string' ? help.trim() : '';
};

/**
 * A field label with its help text, on every surface that names an element:
 * the entry form's inputs, the driven-value row, the read view's rows.
 *
 * Two doors to the same words, because the platform is read with and without
 * a hovering pointer: the label group shows the text as a hover tooltip, and
 * a "?" button beside the label opens it in a click popover for touch
 * screens and keyboards (Enter/Space open it, Esc closes it). The tooltip
 * yields while the popover is open so the text never shows twice.
 *
 * The button sits BESIDE the label rather than inside it: a button is a
 * labelable element, so nesting one in a <label> is invalid HTML — and a
 * click on it would activate the labelled control (open the select, focus
 * the input) along with the popover.
 *
 * With no text, `children` render exactly as they would without this
 * wrapper — no extra element, no handlers — so a field without help is
 * untouched.
 */
export default function FieldHelp({ text, label, children, className = '' }) {
	const [tipOpen, setTipOpen] = useState(false);
	const [popOpen, setPopOpen] = useState(false);

	if (!text) return children;

	const name = label || 'this field';

	return (
		<Tooltip
			title={text}
			open={tipOpen && !popOpen}
			onOpenChange={setTipOpen}
			placement='top'
			overlayClassName='field-help__tip'
		>
			<div className={`field-help ${className}`.trim()}>
				{children}
				<Popover
					title={name}
					content={<div className='field-help__content'>{text}</div>}
					trigger='click'
					placement='top'
					open={popOpen}
					onOpenChange={setPopOpen}
					overlayClassName='field-help__pop'
				>
					<button
						type='button'
						className='field-help__trigger'
						aria-label={`About ${name}`}
						aria-haspopup='dialog'
						aria-expanded={popOpen}
						onKeyDown={e => {
							if (e.key === 'Escape' && popOpen) {
								// Ours to close; the form's own Esc (cancel) must not fire.
								e.stopPropagation();
								setPopOpen(false);
							}
						}}
					>
						<HELP_QUESTION_MARK aria-hidden='true' />
					</button>
				</Popover>
			</div>
		</Tooltip>
	);
}
